import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Button, Paper, Alert, CircularProgress, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl } from '../../config/api';

const PageTitle = styled(Typography)({
  fontFamily: 'Cormorant Garamond, serif',
  fontSize: '2.5rem',
  color: '#1a1a1a',
  marginBottom: '2rem',
  textAlign: 'center',
});

const StyledButton = styled(Button)({
  backgroundColor: '#1a1a1a',
  color: '#fff',
  padding: '1rem 2rem',
  '&:hover': {
    backgroundColor: '#c4a661',
  },
});

const SignOutButton = styled(Button)({
  backgroundColor: 'transparent',
  color: '#1a1a1a',
  padding: '1rem 2rem',
  border: '1px solid #1a1a1a',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#c4a661',
    color: '#c4a661',
  },
});

const ContentWrapper = styled(Paper)({
  padding: '2rem',
  marginBottom: '2rem',
  backgroundColor: '#fafafa',
  border: '1px solid #eee',
  borderRadius: '4px',
});

const OrderCard = styled(Paper)({
  padding: '1.5rem',
  marginBottom: '1rem',
  border: '1px solid #eee',
  borderRadius: '4px',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
});

const Profile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user) {
      setLoading(true);
      const encodedEmail = encodeURIComponent(user.email);
      fetch(getApiUrl(`/api/orders/${encodedEmail}`))
        .then(async res => {
          const text = await res.text();
          console.log('Raw response:', text);
          
          if (!res.ok) {
            try {
              const errorData = JSON.parse(text);
              throw new Error(errorData.error || 'Failed to load order history');
            } catch (e) {
              throw new Error(`Failed to load order history: ${text}`);
            }
          }
          
          return JSON.parse(text);
        })
        .then(data => {
          console.log('Orders data:', data);
          setOrders(data.orders || []);
          setLoading(false);
        })
        .catch(err => {
          console.error('Error fetching orders:', err);
          setError(err.message);
          setLoading(false);
        });
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
      setError('Failed to sign out. Please try again.');
    }
  };

  if (!user) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PageTitle>My Account</PageTitle>
            
            <ContentWrapper>
              <Alert severity="info" sx={{ mb: 3 }}>
                Sign in to view your order history and manage your account.
              </Alert>

              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <StyledButton
                  component={Link}
                  to="/login"
                  variant="contained"
                >
                  Sign In
                </StyledButton>
                
                <StyledButton
                  component={Link}
                  to="/register"
                  variant="outlined"
                  sx={{
                    backgroundColor: 'transparent',
                    borderColor: '#1a1a1a',
                    color: '#1a1a1a',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      borderColor: '#c4a661',
                      color: '#c4a661',
                    },
                  }}
                >
                  Create Account
                </StyledButton>
              </Box>
            </ContentWrapper>
          </motion.div>
        </Box>
      </Container>
    );
  }

  const formatPrice = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount / 100);
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 8 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <PageTitle>My Account</PageTitle>
          
          <ContentWrapper>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
              <Typography variant="h6">
                Welcome back, {user.displayName || user.email}!
              </Typography>
              <SignOutButton onClick={handleSignOut}>
                Sign Out
              </SignOutButton>
            </Box>

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Account Details
              </Typography>
              <Typography>
                Email: {user.email}
              </Typography>
              {user.displayName && (
                <Typography>
                  Name: {user.displayName}
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 4 }} />

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Order History
              </Typography>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              ) : orders.length === 0 ? (
                <Alert severity="info" sx={{ mb: 3 }}>
                  No orders found. Start shopping to see your order history here.
                </Alert>
              ) : (
                orders.map((order) => (
                  <OrderCard key={order.id} elevation={0}>
                    <Typography variant="subtitle1" gutterBottom>
                      Order #{order.id}
                    </Typography>
                    <Typography color="textSecondary" gutterBottom>
                      {formatDate(order.created)}
                    </Typography>
                    <Typography gutterBottom>
                      Total: {formatPrice(order.amount_total, order.currency)}
                    </Typography>
                    <Typography color="textSecondary">
                      Status: {order.status}
                    </Typography>
                  </OrderCard>
                ))
              )}
            </Box>
          </ContentWrapper>
        </motion.div>
      </Box>
    </Container>
  );
};

export default Profile;
